import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ArrowDown, ArrowUp, Image as ImageIcon, Loader2, Plus, Trash2, Upload, X } from "lucide-react";
import { BLOCK_LABELS, emptyBlock, errorMessage, uploadCommsImage, type TemplateBlock } from "@/lib/comms";

type FieldName = "text" | "url" | "alt" | "width" | "height" | "item";

interface FocusTarget {
  el: HTMLInputElement | HTMLTextAreaElement;
  index: number;
  field: FieldName;
  sub: number | null;
}

const ADD_ORDER: TemplateBlock["type"][] = ["paragraph", "heading", "image", "button", "callout", "bullets", "divider"];

/**
 * A block-by-block way of writing the same plain-text markup the server renders. It never owns
 * the body: the parent holds the markup, this only hands back a new block list.
 */
export default function BlockEditor({
  blocks,
  onChange,
  insertRequest,
}: {
  blocks: TemplateBlock[];
  onChange: (blocks: TemplateBlock[]) => void;
  /** Bump the nonce to drop a {{variable}} into whichever field was last focused. */
  insertRequest?: { token: string; nonce: number } | null;
}) {
  const { toast } = useToast();
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const focusRef = useRef<FocusTarget | null>(null);
  const lastNonce = useRef(insertRequest?.nonce ?? 0);
  const [uploading, setUploading] = useState<number | null>(null);
  const fileInputs = useRef<Record<number, HTMLInputElement | null>>({});

  const replace = (index: number, next: TemplateBlock) => {
    onChange(blocksRef.current.map((b, i) => (i === index ? next : b)));
  };

  const patch = (index: number, changes: Record<string, unknown>) => {
    const current = blocksRef.current[index];
    if (!current) return;
    replace(index, { ...current, ...changes } as TemplateBlock);
  };

  const move = (index: number, delta: number) => {
    const next = [...blocksRef.current];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const remove = (index: number) => onChange(blocksRef.current.filter((_, i) => i !== index));

  const add = (type: TemplateBlock["type"]) => onChange([...blocksRef.current, emptyBlock(type)]);

  const track = (index: number, field: FieldName, sub: number | null = null) =>
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      focusRef.current = { el: e.currentTarget, index, field, sub };
    };

  // Variable insertion: write into the live DOM value so nothing typed since focus is lost.
  useEffect(() => {
    if (!insertRequest || insertRequest.nonce === lastNonce.current) return;
    lastNonce.current = insertRequest.nonce;
    const token = insertRequest.token;
    const target = focusRef.current;

    if (!target || !document.contains(target.el)) {
      onChange([...blocksRef.current, { type: "paragraph", text: token }]);
      return;
    }

    const { el, index, field, sub } = target;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    const value = `${el.value.slice(0, start)}${token}${el.value.slice(end)}`;
    const block = blocksRef.current[index];
    if (!block) return;

    if (block.type === "bullets" && field === "item" && sub !== null) {
      replace(index, { ...block, items: block.items.map((it, i) => (i === sub ? value : it)) });
    } else {
      patch(index, { [field]: value });
    }

    requestAnimationFrame(() => {
      el.focus();
      const caret = start + token.length;
      el.setSelectionRange(caret, caret);
    });
  }, [insertRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (index: number, file: File) => {
    setUploading(index);
    try {
      const res = await uploadCommsImage(file);
      patch(index, { url: res.url });
      toast({ title: "Image uploaded", description: "The link is in the block. Set a width so it does not fill the message." });
    } catch (e) {
      toast({ title: "Upload failed", description: errorMessage(e), variant: "destructive" });
    } finally {
      setUploading(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="block-editor">
      {blocks.length === 0 && (
        <div className="border rounded-md p-6 text-sm text-muted-foreground text-center">
          Nothing here yet. Add a block below and start writing.
        </div>
      )}

      {blocks.map((block, index) => (
        <div key={index} className="border rounded-md p-3 space-y-2" data-testid={`block-${index}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{BLOCK_LABELS[block.type]}</span>
            <div className="flex gap-0.5">
              <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" title="Move up" disabled={index === 0} onClick={() => move(index, -1)}>
                <ArrowUp className="w-3.5 h-3.5" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                title="Move down"
                disabled={index === blocks.length - 1}
                onClick={() => move(index, 1)}
              >
                <ArrowDown className="w-3.5 h-3.5" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                title="Delete this block"
                onClick={() => remove(index)}
                data-testid={`button-remove-block-${index}`}
              >
                <Trash2 className="w-3.5 h-3.5 text-destructive" />
              </Button>
            </div>
          </div>

          {block.type === "paragraph" && (
            <Textarea
              value={block.text}
              onChange={e => patch(index, { text: e.target.value })}
              onFocus={track(index, "text")}
              rows={3}
              placeholder="Hi {{firstName}}, here is how {{childFirstName}} did this week."
              className="text-sm"
              data-testid={`input-block-paragraph-${index}`}
            />
          )}

          {block.type === "heading" && (
            <div className="flex gap-2">
              <Select value={String(block.level)} onValueChange={v => patch(index, { level: Number(v) === 3 ? 3 : 2 })}>
                <SelectTrigger className="w-[100px]" aria-label="Heading level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">H2</SelectItem>
                  <SelectItem value="3">H3</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={block.text}
                onChange={e => patch(index, { text: e.target.value })}
                onFocus={track(index, "text")}
                placeholder="This week at a glance"
                data-testid={`input-block-heading-${index}`}
              />
            </div>
          )}

          {block.type === "image" && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  value={block.url}
                  onChange={e => patch(index, { url: e.target.value })}
                  onFocus={track(index, "url")}
                  placeholder="https://…/owlbert.png"
                  className="font-mono text-xs"
                  data-testid={`input-block-image-url-${index}`}
                />
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  className="hidden"
                  ref={el => { fileInputs.current[index] = el; }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (file) upload(index, file);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={uploading === index}
                  onClick={() => fileInputs.current[index]?.click()}
                  data-testid={`button-upload-image-${index}`}
                >
                  {uploading === index ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload
                </Button>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <div className="space-y-1">
                  <Label className="text-xs">Width, px</Label>
                  <Input
                    value={block.width}
                    onChange={e => patch(index, { width: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                    onFocus={track(index, "width")}
                    placeholder="520"
                    inputMode="numeric"
                    data-testid={`input-block-image-width-${index}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Height, px</Label>
                  <Input
                    value={block.height}
                    onChange={e => patch(index, { height: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                    onFocus={track(index, "height")}
                    placeholder="Optional"
                    inputMode="numeric"
                    data-testid={`input-block-image-height-${index}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Alt text</Label>
                  <Input
                    value={block.alt}
                    onChange={e => patch(index, { alt: e.target.value })}
                    onFocus={track(index, "alt")}
                    placeholder="What the picture shows"
                    data-testid={`input-block-image-alt-${index}`}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Width and height together stop Gmail treating the picture as a download. A height on its own cannot be written into the markup, so
                set a width whenever you set a height.
              </p>
              {block.url ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <ImageIcon className="w-3.5 h-3.5" />
                  <img src={block.url} alt="" className="h-12 w-auto rounded border object-contain bg-muted" />
                </div>
              ) : null}
            </div>
          )}

          {block.type === "button" && (
            <div className="grid gap-2 sm:grid-cols-2">
              <Input
                value={block.text}
                onChange={e => patch(index, { text: e.target.value })}
                onFocus={track(index, "text")}
                placeholder="See the report"
                data-testid={`input-block-button-text-${index}`}
              />
              <Input
                value={block.url}
                onChange={e => patch(index, { url: e.target.value })}
                onFocus={track(index, "url")}
                placeholder="https://edufocalacademy.com/parent"
                className="font-mono text-xs"
                data-testid={`input-block-button-url-${index}`}
              />
            </div>
          )}

          {block.type === "callout" && (
            <Textarea
              value={block.text}
              onChange={e => patch(index, { text: e.target.value })}
              onFocus={track(index, "text")}
              rows={2}
              placeholder="One line that deserves its own box."
              className="text-sm"
              data-testid={`input-block-callout-${index}`}
            />
          )}

          {block.type === "bullets" && (
            <div className="space-y-2">
              {block.items.map((item, sub) => (
                <div key={sub} className="flex gap-2 items-center">
                  <span className="text-muted-foreground text-sm">•</span>
                  <Input
                    value={item}
                    onChange={e => replace(index, { ...block, items: block.items.map((it, i) => (i === sub ? e.target.value : it)) })}
                    onFocus={track(index, "item", sub)}
                    placeholder="One point per line"
                    data-testid={`input-block-bullet-${index}-${sub}`}
                  />
                  {block.items.length > 1 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      title="Remove this bullet"
                      onClick={() => replace(index, { ...block, items: block.items.filter((_, i) => i !== sub) })}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => replace(index, { ...block, items: [...block.items, ""] })}
                data-testid={`button-add-bullet-${index}`}
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add bullet
              </Button>
            </div>
          )}

          {block.type === "divider" && <hr className="border-dashed" />}
        </div>
      ))}

      <div className="flex flex-wrap gap-1.5">
        {ADD_ORDER.map(type => (
          <Button
            key={type}
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            onClick={() => add(type)}
            data-testid={`button-add-block-${type}`}
          >
            <Plus className="w-3 h-3 mr-1" /> {BLOCK_LABELS[type]}
          </Button>
        ))}
      </div>
    </div>
  );
}
